import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { IconButton } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  INTAKE_FORM_TITLE_INPUT_PLACEHOLDER,
  INTAKE_FORM_TITLE_TEXT_PLACEHOLDER,
} from './intake-form-fields';
import { IntakeCardLead } from './IntakeCardLead';
import { IntakeFormBuilderModal } from './IntakeFormBuilderModal';
import { useIntakeFormTemplate } from './use-intake-form-template';

const useStyles = makeStyles()(theme => ({
  filledCard: {
    '&:hover': {
      borderColor: theme.appColors.primary,
      boxShadow: '0px 1px 8.2px -2px rgba(0, 0, 0, 0.15)',
    },
    alignItems: 'center',
    background: theme.palette.common.white,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    transition: 'border-color 150ms, box-shadow 150ms',
    width: '100%',
  },
  headerActions: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  title: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleInput: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
  },
}));

interface IntakeFormPhaseCardProps {
  intakeFormTemplate: ApolloTypes.IntakeFormTemplateFullFragment;
  workflowId: UUID;
  readOnly?: boolean;
}

export function IntakeFormPhaseCard({
  intakeFormTemplate,
  readOnly = false,
  workflowId,
}: IntakeFormPhaseCardProps) {
  const { classes } = useStyles();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { removeIntakeForm, removing, renameIntakeForm } = useIntakeFormTemplate(workflowId);

  return (
    <>
      <div className={classes.filledCard} onClick={() => !readOnly && setBuilderOpen(true)}>
        <IntakeCardLead>
          <InlineEditField
            classes={{ display: classes.title, input: classes.titleInput }}
            disabled={readOnly}
            editOnDisplayClick={false}
            iconSize={16}
            placeholder={INTAKE_FORM_TITLE_INPUT_PLACEHOLDER}
            textPlaceholder={INTAKE_FORM_TITLE_TEXT_PLACEHOLDER}
            value={intakeFormTemplate.title}
            onSave={title => renameIntakeForm(intakeFormTemplate.id, title)}
          />
        </IntakeCardLead>
        {!readOnly && (
          <div className={classes.headerActions}>
            <AlineTooltip arrow placement="top" title="Remove intake form">
              <span>
                <IconButton
                  aria-label="Remove intake form"
                  disabled={removing}
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    setConfirmRemove(true);
                  }}
                >
                  <Trash2 size={18} />
                </IconButton>
              </span>
            </AlineTooltip>
          </div>
        )}
      </div>

      <IntakeFormBuilderModal
        existingTemplate={intakeFormTemplate}
        open={builderOpen}
        workflowId={workflowId}
        onClose={() => setBuilderOpen(false)}
      />

      <ConfirmationDialog
        message="This removes the intake form from this template. The reusable form itself is kept."
        open={confirmRemove}
        primaryText="Remove"
        title="Remove intake form?"
        onClickPrimary={removeIntakeForm}
        onClose={() => setConfirmRemove(false)}
      />
    </>
  );
}
